import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

interface SectionHeaderProps {
  /**
   * Section title. Omit in tab panels whose tab already names the section
   * (DESIGN.md › Tables › Tab-panel sections) — then only description/action render.
   */
  title?: ReactNode;
  description?: ReactNode;
  /** Right-aligned action (usually the "Add X" button). */
  action?: ReactNode;
  /**
   * `default` is the heading block above a standalone table (h3 title, description below).
   * `strip` is the compact header of one section inside a nested, multi-section list
   * (DESIGN.md › Tables › Multi-section lists › Bracketed variant): a muted bar with the title
   * and the description on one line, the action on the right. Use it when five or more
   * sections stack, where the h3 tier would be too heavy.
   */
  variant?: "default" | "strip";
  className?: string;
}

// The heading block above a standalone table or multi-section list
// (DESIGN.md › Tables): title + description left, action button right.
export function SectionHeader({
  title,
  description,
  action,
  variant = "default",
  className,
}: SectionHeaderProps) {
  if (variant === "strip") {
    return (
      <div
        className={cn(
          "flex items-center justify-between gap-4 rounded-md bg-muted/50 px-3 py-2",
          className,
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          {title && <span className="text-sm font-medium">{title}</span>}
          {description && (
            <span className="truncate text-xs text-muted-foreground">· {description}</span>
          )}
        </div>
        {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
      </div>
    );
  }

  return (
    <div className={cn("flex items-center justify-between gap-4", className)}>
      <div>
        {title && <h3 className="text-lg font-semibold">{title}</h3>}
        {description && <p className="text-muted-foreground text-sm">{description}</p>}
      </div>
      {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
    </div>
  );
}
